import React, { useState, useEffect } from 'react';
import {
  Mail, Plus, Trash2, Edit2, Loader2, Check, X, Eye, EyeOff,
  Server, Lock, Shield, Star, RefreshCw, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, Settings, Zap
} from 'lucide-react';
import { emailAccountsAPI } from '../../services/api';

const EmailAccountsConfig = () => {
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    password: '',
    preset: 'ovh',
    smtp_server: '',
    smtp_port: 465,
    smtp_security: 'SSL',
    imap_server: '',
    imap_port: 993,
    imap_security: 'SSL',
    is_default: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [accountsRes, presetsRes] = await Promise.all([
        emailAccountsAPI.getAll(),
        emailAccountsAPI.getPresets()
      ]);
      setAccounts(accountsRes.data || []);
      setPresets(presetsRes.data || {});
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (presetKey) => {
    const preset = presets[presetKey];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        preset: presetKey,
        smtp_server: preset.smtp_server || prev.smtp_server,
        smtp_port: preset.smtp_port || prev.smtp_port,
        smtp_security: preset.smtp_security || prev.smtp_security,
        imap_server: preset.imap_server || prev.imap_server,
        imap_port: preset.imap_port || prev.imap_port,
        imap_security: preset.imap_security || prev.imap_security
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      email: '',
      password: '',
      preset: 'ovh',
      smtp_server: presets.ovh?.smtp_server || 'ssl0.ovh.net',
      smtp_port: presets.ovh?.smtp_port || 465,
      smtp_security: presets.ovh?.smtp_security || 'SSL',
      imap_server: presets.ovh?.imap_server || 'ssl0.ovh.net',
      imap_port: presets.ovh?.imap_port || 993,
      imap_security: presets.ovh?.imap_security || 'SSL',
      is_default: accounts.length === 0
    });
    setShowPassword(false);
    setTestResults(null);
  };

  const handleTestConnection = async () => {
    setTesting('form');
    setTestResults(null);
    try {
      const res = await emailAccountsAPI.testConnection({
        email: formData.email,
        password: formData.password,
        smtp_server: formData.smtp_server,
        smtp_port: formData.smtp_port,
        smtp_security: formData.smtp_security,
        imap_server: formData.imap_server,
        imap_port: formData.imap_port,
        imap_security: formData.imap_security
      });
      setTestResults(res.data);
    } catch (error) {
      setTestResults({
        smtp: { success: false, message: error.response?.data?.detail || 'Erreur de connexion' },
        imap: { success: false, message: error.response?.data?.detail || 'Erreur de connexion' }
      });
    } finally {
      setTesting(null);
    }
  };

  const handleTestExistingAccount = async (accountId) => {
    setTesting(accountId);
    try {
      const res = await emailAccountsAPI.testAccount(accountId);
      setTestResults({ accountId, ...res.data });
    } catch (error) {
      setTestResults({
        accountId,
        smtp: { success: false, message: 'Erreur' },
        imap: { success: false, message: 'Erreur' }
      });
    } finally {
      setTesting(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) return;

    setSaving(true);
    try {
      if (editingAccount) {
        await emailAccountsAPI.update(editingAccount.id, formData);
      } else {
        await emailAccountsAPI.create(formData);
      }
      await loadData();
      setShowAddForm(false);
      setEditingAccount(null);
      resetForm();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      nom: account.nom,
      email: account.email,
      password: '', // Ne pas pré-remplir le mot de passe
      preset: account.preset || 'custom',
      smtp_server: account.smtp_server,
      smtp_port: account.smtp_port,
      smtp_security: account.smtp_security,
      imap_server: account.imap_server,
      imap_port: account.imap_port,
      imap_security: account.imap_security,
      is_default: account.is_default
    });
    setShowAddForm(true);
    setTestResults(null);
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Supprimer ce compte email ?')) return;
    
    try {
      await emailAccountsAPI.delete(accountId);
      await loadData();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      await emailAccountsAPI.setDefault(accountId);
      await loadData();
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Comptes Email</h2>
              <p className="text-indigo-100">Configuration SMTP/IMAP pour l'envoi et la réception</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {accounts.length} compte{accounts.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Liste des comptes */}
      {accounts.length > 0 && (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.id}
              className={`bg-white rounded-xl border-2 transition overflow-hidden ${
                account.is_default ? 'border-indigo-300 shadow-md' : 'border-gray-200'
              }`}
            >
              {/* Header du compte */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedAccount(expandedAccount === account.id ? null : account.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    account.is_default ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    <Mail className={`w-5 h-5 ${account.is_default ? 'text-indigo-600' : 'text-gray-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{account.nom}</span>
                      {account.is_default && (
                        <span className="flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                          <Star className="w-3 h-3" /> Par défaut
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{account.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {presets[account.preset]?.name || account.preset}
                  </span>
                  {expandedAccount === account.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Détails expandés */}
              {expandedAccount === account.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="text-xs font-medium text-gray-500 mb-1">SMTP (Envoi)</div>
                      <div className="text-sm font-mono text-gray-900">
                        {account.smtp_server}:{account.smtp_port}
                      </div>
                      <div className="text-xs text-gray-500">{account.smtp_security}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <div className="text-xs font-medium text-gray-500 mb-1">IMAP (Réception)</div>
                      <div className="text-sm font-mono text-gray-900">
                        {account.imap_server}:{account.imap_port}
                      </div>
                      <div className="text-xs text-gray-500">{account.imap_security}</div>
                    </div>
                  </div>

                  {/* Résultats de test pour ce compte */}
                  {testResults?.accountId === account.id && (
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className={`p-2 rounded-lg flex items-center gap-2 text-sm ${
                        testResults.smtp?.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {testResults.smtp?.success ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        SMTP: {testResults.smtp?.message}
                      </div>
                      <div className={`p-2 rounded-lg flex items-center gap-2 text-sm ${
                        testResults.imap?.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {testResults.imap?.success ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        IMAP: {testResults.imap?.message}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleTestExistingAccount(account.id)}
                      disabled={testing === account.id}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm"
                    >
                      {testing === account.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4" />
                      )}
                      Tester
                    </button>
                    {!account.is_default && (
                      <button
                        onClick={() => handleSetDefault(account.id)}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition text-sm"
                      >
                        <Star className="w-4 h-4" />
                        Définir par défaut
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(account)}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm"
                    >
                      <Edit2 className="w-4 h-4" />
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(account.id)}
                      className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Message si aucun compte */}
      {accounts.length === 0 && !showAddForm && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun compte email configuré</h3>
          <p className="text-gray-500 mb-4">Ajoutez un compte email pour commencer à envoyer et recevoir des emails</p>
          <button
            onClick={() => { resetForm(); setShowAddForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Plus className="w-5 h-5" />
            Ajouter un compte
          </button>
        </div>
      )}

      {/* Bouton ajouter (quand il y a déjà des comptes) */}
      {accounts.length > 0 && !showAddForm && (
        <button
          onClick={() => { resetForm(); setShowAddForm(true); }}
          className="w-full py-4 border-2 border-dashed border-indigo-300 rounded-xl text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Ajouter un compte email
        </button>
      )}

      {/* Formulaire d'ajout/modification */}
      {showAddForm && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">
              {editingAccount ? 'Modifier le compte' : 'Nouveau compte email'}
            </h3>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Preset hébergeur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hébergeur email</label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(presets).map(([key, preset]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handlePresetChange(key)}
                    className={`p-3 rounded-lg border-2 text-center transition ${
                      formData.preset === key
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium">{preset.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Informations principales */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du compte *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Contact SocialAC"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@socialac.fr"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mot de passe {editingAccount ? '(laisser vide pour ne pas changer)' : '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required={!editingAccount}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Configuration serveurs */}
            <div className="grid grid-cols-2 gap-6">
              {/* SMTP */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">SMTP (Envoi)</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-blue-700 mb-1">Serveur</label>
                    <input
                      type="text"
                      value={formData.smtp_server}
                      onChange={(e) => setFormData({ ...formData, smtp_server: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="ssl0.ovh.net"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-blue-700 mb-1">Port</label>
                      <input
                        type="number"
                        value={formData.smtp_port}
                        onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-blue-700 mb-1">Sécurité</label>
                      <select
                        value={formData.smtp_security}
                        onChange={(e) => setFormData({ ...formData, smtp_security: e.target.value })}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="SSL">SSL</option>
                        <option value="STARTTLS">STARTTLS</option>
                        <option value="None">Aucune</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* IMAP */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-900">IMAP (Réception)</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-green-700 mb-1">Serveur</label>
                    <input
                      type="text"
                      value={formData.imap_server}
                      onChange={(e) => setFormData({ ...formData, imap_server: e.target.value })}
                      className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      placeholder="ssl0.ovh.net"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-green-700 mb-1">Port</label>
                      <input
                        type="number"
                        value={formData.imap_port}
                        onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-green-700 mb-1">Sécurité</label>
                      <select
                        value={formData.imap_security}
                        onChange={(e) => setFormData({ ...formData, imap_security: e.target.value })}
                        className="w-full px-3 py-2 border border-green-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                      >
                        <option value="SSL">SSL</option>
                        <option value="STARTTLS">STARTTLS</option>
                        <option value="None">Aucune</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Compte par défaut */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="is_default" className="text-sm text-gray-700">
                Définir comme compte par défaut
              </label>
            </div>

            {/* Résultats du test */}
            {testResults && !testResults.accountId && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Résultat du test</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg ${
                    testResults.smtp?.success ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {testResults.smtp?.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${testResults.smtp?.success ? 'text-green-800' : 'text-red-800'}`}>
                        SMTP
                      </span>
                    </div>
                    <p className={`text-sm ${testResults.smtp?.success ? 'text-green-700' : 'text-red-700'}`}>
                      {testResults.smtp?.message}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${
                    testResults.imap?.success ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {testResults.imap?.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${testResults.imap?.success ? 'text-green-800' : 'text-red-800'}`}>
                        IMAP
                      </span>
                    </div>
                    <p className={`text-sm ${testResults.imap?.success ? 'text-green-700' : 'text-red-700'}`}>
                      {testResults.imap?.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={!formData.email || !formData.password || testing === 'form'}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition disabled:opacity-50"
              >
                {testing === 'form' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Zap className="w-5 h-5" />
                )}
                Tester la connexion
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setEditingAccount(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.email || (!editingAccount && !formData.password)}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5" />
                  )}
                  {editingAccount ? 'Enregistrer' : 'Créer le compte'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EmailAccountsConfig;
